'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useViewport } from '@/hooks';
import { WithLabel } from '../label/WithLabel';
import { MultiSelectOnDrawerUI } from './MultiSelectOnDrawerUI';
import { MultiSelectUI } from './MultiSelectUI';
import { type MultiSelectProps, type SelectOption } from './type';

/**
 * 기본 다중 선택 컴포넌트
 *
 * @param values - 선택된 값 state (배열)
 * @param options - 선택 옵션 배열
 * @param onValueChange - 선택 값 변경 핸들러
 * @param placeholder - 선택 옵션이 없을 때 표시할 텍스트
 * @param error - 외부에서 주입되는 에러 여부
 * @param onError - 선택 옵션이 유효하지 않을 때 호출되는 핸들러
 * @param title - 선택 컴포넌트의 제목
 * @param titleLeftSlot - 선택 컴포넌트의 제목 왼쪽 슬롯
 * @param titleRightSlot - 선택 컴포넌트의 제목 오른쪽 슬롯
 * @param showScrollbar - 스크롤바 표시 여부
 * @param hasDrawerHandle - 드로어 핸들 표시 여부
 * @param maxCount - 최대 선택 개수
 * @param valueRenderer - 선택된 값을 보여줄 렌더 함수
 * @param trigger - 선택 컴포넌트의 커스텀 트리거
 *
 * @example
 * ```
 * <MultiSelect
 *    values={values}
 *    options={options}
 *    onValuesChange={values => setValues(values)}
 * />
 * ```
 */
export const MultiSelect = <T extends string,>(props: MultiSelectProps<T>) => {
  const {
    isMobile,
    isMobileSm
  } = useViewport();

  // 기본은 SingleSelect 와 같은 규칙 — 아주 좁은 화면(isMobileSm)에서만 드로어로 열고,
  // "dropdown" 이 명시되면 중첩 드로어를 피해 드롭다운으로 연다.
  //
  // "drawer" 를 명시하면 모바일 전 구간(isMobile, ≤560px)에서 드로어로 연다.
  // 항목이 한 줄로 안 끝나는 목록(아바타 + 이름 + 상태)은 트리거 밑 드롭다운으로 열면
  // 좁아서 못 읽는데, 기본 기준(≤420px)만으로는 420~560 구간이 드롭다운으로 남는다.
  const shouldUseDrawer = props.mobilePresentation === 'drawer' ? isMobile : isMobileSm && props.mobilePresentation !== 'dropdown';
  const MultiSelectUIComp = shouldUseDrawer ? MultiSelectOnDrawerUI : MultiSelectUI;
  const {
    isOpen,
    open,
    close,
    handleError,
    handleSelect,
    isError,
    renderedValue
  } = useMultiSelect(props);
  return <WithLabel title={props.title} titleLeftSlot={props.titleLeftSlot} titleRightSlot={props.titleRightSlot} description={props.description} helperText={props.helperText} error={isError} data-sentry-element="WithLabel" data-sentry-component="MultiSelect" data-sentry-source-file="MultiSelect.tsx">
      <MultiSelectUIComp {...props} isOpen={isOpen} open={open} close={close} onError={handleError} onSelect={handleSelect} error={isError} renderedValue={renderedValue} data-sentry-element="MultiSelectUIComp" data-sentry-source-file="MultiSelect.tsx" />
    </WithLabel>;
};

/**
 * 커스텀 Select UI에 인터랙션 로직을 추가하기 위한 hook
 *
 * 기본 Select UI 뿐만 아니라, dropdown, dialog, drawer 등 다양한 UI에 적용 가능
 *
 * @param props - 커스텀 Select UI 컴포넌트의 props
 */
export function useMultiSelect<T extends string>(props: MultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const {
    values,
    onValuesChange,
    maxCount,
    options,
    onError,
    valueRenderer
  } = props;
  const handleSelect = useCallback((value: T) => {
    if (values.includes(value)) {
      onValuesChange(values.filter(v => v !== value));
    } else {
      onValuesChange([...values, value]);
      //maxCount 도달 시 컨텐츠 닫기
      maxCount != null && maxCount !== 0 && values.length === maxCount - 1 && setIsOpen(false);
    }
  }, [values, onValuesChange, maxCount]);
  useEffect(() => {
    const isInvalidIncluded = values.find(selected => options.find(option => option.value === selected)?.invalid);
    if (isInvalidIncluded) {
      setIsInvalid(true);
      onError?.();
    } else {
      setIsInvalid(false);
    }
  }, [values, options, onError]);
  const renderedValue = useMemo(() => {
    //선택된 값이 없으면 placeholder를 보여줌
    if (values.length === 0) return undefined;
    const selectedOptions = values.map(v => options.find(o => o.value === v));

    //외부에서 들어오는 custom valuesRenderer가 있다면 그것을 사용
    if (valueRenderer) {
      return valueRenderer(selectedOptions as SelectOption<T>[]);
    }

    //default text value는 선택된 값이 있으면 그것을 콤마로 구분하여 보여줌
    return selectedOptions.map(o => o?.label).join(', ');
  }, [values, options, valueRenderer]);
  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    handleError: () => setIsInvalid(true),
    handleSelect,
    isError: isInvalid || props.error,
    renderedValue
  };
}